import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Form,
  Header,
  Icon,
  Label,
  Loader,
  Message,
  Modal,
  Table,
} from 'semantic-ui-react';
import { API } from '../../helpers';
import './OrganizationInvitationsV108.css';

const initialQuery = { page: 1, page_size: 10, status: '', q: '' };
// Keep business-error envelopes; leave authentication failures to the shared API.
const invitationRequestOptions = {
  validateStatus: (status) => (status >= 200 && status < 300) || status === 400,
};
const failureMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;
const dateLabel = (seconds, language) =>
  seconds
    ? new Date(seconds * 1000).toLocaleString(language || undefined)
    : '-';

export const validInvitationCreditV109 = (value) => {
  if (!/^\d+(\.\d{1,9})?$/.test(value) || value.length > 32) return false;
  const [whole, fraction = ''] = value.split('.');
  return (
    window.BigInt(whole + fraction.padEnd(9, '0')) <=
    window.BigInt('9223372036854775807')
  );
};

const creditNano = (value) => {
  if (!validInvitationCreditV109(value)) return null;
  const [whole, fraction = ''] = value.split('.');
  return window.BigInt(whole + fraction.padEnd(9, '0'));
};

export const invitationReserveTotalV1010 = (credit, count, uses) => {
  const amount = creditNano(credit);
  if (
    amount === null ||
    !Number.isInteger(Number(count)) ||
    Number(count) < 1 ||
    Number(count) > 100 ||
    !Number.isInteger(Number(uses)) ||
    Number(uses) < 1 ||
    Number(uses) > 1000000
  )
    return null;
  const total =
    amount * window.BigInt(Number(count)) * window.BigInt(Number(uses));
  if (total > window.BigInt('9223372036854775807')) return null;
  const digits = total.toString().padStart(10, '0');
  const fraction = digits.slice(-9).replace(/0+$/, '').padEnd(2, '0');
  return `${digits.slice(0, -9)}.${fraction}`;
};

function useInvitationList(endpoint, query, revision, fallback) {
  const key = `${endpoint}?${new URLSearchParams(query)}&revision=${revision}`;
  const [result, setResult] = useState({
    key: '',
    loading: true,
    data: null,
    error: '',
  });
  useEffect(() => {
    let current = true;
    setResult({ key, loading: true, data: null, error: '' });
    const load = async () => {
      try {
        const response = await API.get(
          `${endpoint}?${new URLSearchParams(query)}`,
          invitationRequestOptions
        );
        if (!current) return;
        if (
          !response?.data?.success ||
          !Array.isArray(response.data.data?.items)
        ) {
          throw new Error(response?.data?.message || fallback);
        }
        setResult({ key, loading: false, data: response.data.data, error: '' });
      } catch (error) {
        if (current)
          setResult({
            key,
            loading: false,
            data: null,
            error: failureMessage(error, fallback),
          });
      }
    };
    load();
    return () => {
      // Cancel delivery, not transport: the shared API interceptor toasts aborts.
      current = false;
    };
  }, [endpoint, query, revision, fallback, key]);
  // Never render a previous query's rows while the next effect is starting.
  return result.key === key ? result : { loading: true, data: null, error: '' };
}

function ListPager({ query, data, loading, onChange, text }) {
  const total = Number(data?.total || 0);
  const pages = Math.max(1, Math.ceil(total / query.page_size));
  return (
    <div className='invitation-pager-v108'>
      <span>
        {text('共', 'Total')} {total}
      </span>
      <label>
        {text('每页', 'Page size')}
        <select
          aria-label={text('每页', 'Page size')}
          value={query.page_size}
          onChange={(event) =>
            onChange({
              ...query,
              page: 1,
              page_size: Number(event.target.value),
            })
          }
        >
          {[10, 20, 50].map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </label>
      <div className='invitation-page-buttons-v108'>
        <Button
          icon
          type='button'
          title={text('上一页', 'Previous page')}
          aria-label={text('上一页', 'Previous page')}
          disabled={loading || query.page <= 1}
          onClick={() => onChange({ ...query, page: query.page - 1 })}
        >
          <Icon name='chevron left' />
        </Button>
        <span aria-live='polite'>
          {query.page} / {pages}
        </span>
        <Button
          icon
          type='button'
          title={text('下一页', 'Next page')}
          aria-label={text('下一页', 'Next page')}
          disabled={loading || query.page >= pages}
          onClick={() => onChange({ ...query, page: query.page + 1 })}
        >
          <Icon name='chevron right' />
        </Button>
      </div>
    </div>
  );
}

function InvitationWorkspace({ admin, organizationId, onSummaryChange, mode }) {
  const { i18n } = useTranslation();
  const chinese = i18n?.language?.startsWith('zh');
  const text = useCallback((zh, en) => (chinese ? zh : en), [chinese]);
  const creationMode = mode === 'organizationCreation';
  const maximumUses = creationMode ? 10000 : 1000000;
  const endpoint = creationMode
    ? '/api/billing/v8/admin/organization-creation-invitations'
    : admin
    ? `/api/billing/v8/admin/organizations/${encodeURIComponent(
        organizationId
      )}/invitations`
    : '/api/billing/v8/organization/invitations';
  const [query, setQuery] = useState(initialQuery);
  const [recordQuery, setRecordQuery] = useState(initialQuery);
  const [search, setSearch] = useState('');
  const [recordSearch, setRecordSearch] = useState('');
  const [revision, setRevision] = useState(0);
  const [recordRevision, setRecordRevision] = useState(0);
  const [count, setCount] = useState('1');
  const [maxUses, setMaxUses] = useState('1');
  const [initialCredit, setInitialCredit] = useState('0');
  const [days, setDays] = useState('7');
  const [note, setNote] = useState('');
  const [codes, setCodes] = useState([]);
  const codeRevision = useRef(0);
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  const [saving, setSaving] = useState(false);
  const pending = useRef(false);
  const mounted = useRef(true);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const list = useInvitationList(
    endpoint,
    query,
    revision,
    text('邀请码加载失败，请重试', 'Could not load invitations. Try again.')
  );
  const records = useInvitationList(
    `${endpoint}/redemptions`,
    recordQuery,
    recordRevision,
    text(
      '兑换记录加载失败，请重试',
      'Could not load redemption records. Try again.'
    )
  );
  const summary = creationMode ? null : list.data?.summary;
  const funds = creationMode ? null : list.data?.funds;
  const reserveTotal = invitationReserveTotalV1010(
    initialCredit,
    count,
    maxUses
  );
  const clearCodes = () => {
    codeRevision.current += 1;
    setCodes([]);
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (!creationMode && list.data?.summary) {
      onSummaryChange?.(list.data.summary);
    }
  }, [creationMode, list.data, onSummaryChange]);

  useEffect(() => {
    if (!list.data) return;
    const lastPage = Math.max(1, Math.ceil(list.data.total / query.page_size));
    if (query.page > lastPage)
      setQuery((current) => ({ ...current, page: lastPage }));
  }, [list.data, query.page, query.page_size]);
  useEffect(() => {
    if (!records.data) return;
    const lastPage = Math.max(
      1,
      Math.ceil(records.data.total / recordQuery.page_size)
    );
    if (recordQuery.page > lastPage)
      setRecordQuery((current) => ({ ...current, page: lastPage }));
  }, [records.data, recordQuery.page, recordQuery.page_size]);
  useEffect(() => {
    const expiries = [...(list.data?.items || []), ...codes]
      .filter((item) => !item.status || item.status === 'active')
      .map((item) => Number(item.expires_at))
      .filter((expiry) => expiry > now);
    if (!expiries.length) return;
    const timer = setTimeout(() => {
      const currentTime = Math.floor(Date.now() / 1000);
      setNow(currentTime);
      codeRevision.current += 1;
      setCodes((current) =>
        current.filter((item) => item.expires_at > currentTime)
      );
      setRevision((value) => value + 1);
    }, Math.min(2147483647, Math.max(1, Math.min(...expiries) * 1000 - Date.now())));
    return () => clearTimeout(timer);
  }, [codes, list.data, now]);

  const refresh = () => {
    clearCodes();
    setRevision((value) => value + 1);
  };
  const changeQuery = (value) => {
    clearCodes();
    setQuery(value);
  };
  const refreshAfterMutation = () => {
    setRevision((value) => value + 1);
    setRecordRevision((value) => value + 1);
  };

  const create = async (event) => {
    event?.preventDefault();
    if (pending.current) return;
    setError('');
    setNotice('');
    const amount = Number(count);
    const uses = Number(maxUses);
    const duration = Number(days);
    if (!creationMode && !validInvitationCreditV109(initialCredit)) {
      setError(
        text(
          '注册充值金额无效，最多支持 9 位小数',
          'Invalid registration credit; use at most 9 decimal places.'
        )
      );
      return;
    }
    if (
      !Number.isInteger(amount) ||
      amount < 1 ||
      amount > 100 ||
      !Number.isInteger(uses) ||
      uses < 1 ||
      uses > maximumUses ||
      !Number.isInteger(duration) ||
      duration < 1 ||
      duration > 365 ||
      Array.from(note.trim()).length > 200
    ) {
      setError(
        text(
          `数量须为 1-100，每个码可用次数须为 1-${maximumUses}，有效期须为 1-365 天，备注最多 200 字`,
          `Count must be 1-100, uses per code 1-${maximumUses}, expiry 1-365 days, and note at most 200 characters.`
        )
      );
      return;
    }
    if (creationMode && (list.loading || list.error)) {
      setError(
        text(
          '邀请码列表尚不可用，请刷新后重试',
          'Invitation list is unavailable. Refresh and try again.'
        )
      );
      return;
    }
    if (
      !creationMode &&
      (!Number.isInteger(summary?.available) ||
        list.loading ||
        list.error ||
        amount * uses > summary.available)
    ) {
      setError(
        text(
          '可用席位不足或尚未加载，请刷新后重试',
          'Insufficient or unavailable seats. Refresh and try again.'
        )
      );
      return;
    }
    if (!creationMode && reserveTotal === null) {
      setError(
        text(
          '总预留金额超出范围，请减少数量、次数或金额',
          'Total reservation is out of range. Reduce count, uses or amount.'
        )
      );
      return;
    }
    if (
      !creationMode &&
      creditNano(reserveTotal) > window.BigInt(0) &&
      (creditNano(funds?.available_usd || '') === null ||
        creditNano(reserveTotal) > creditNano(funds.available_usd))
    ) {
      setError(
        text(
          '主账号可用余额不足或尚未加载，无法预留邀请码金额',
          'Insufficient or unavailable parent funds to reserve invitation credit.'
        )
      );
      return;
    }
    pending.current = true;
    setSaving(true);
    clearCodes();
    const issuedRevision = codeRevision.current;
    const expiresAt = Math.floor(Date.now() / 1000) + duration * 86400;
    try {
      const response = await API.post(
        endpoint,
        {
          count: amount,
          ...(creationMode || uses !== 1 ? { max_uses: uses } : {}),
          ...(!creationMode && /[1-9]/.test(initialCredit)
            ? { initial_credit_usd: initialCredit }
            : {}),
          expires_at: expiresAt,
          note: note.trim(),
        },
        invitationRequestOptions
      );
      if (!mounted.current) return;
      const data = response?.data?.data;
      if (!response?.data?.success)
        throw new Error(
          response?.data?.message ||
            text(
              '创建失败，请刷新核对后重试',
              'Creation failed. Refresh before trying again.'
            )
        );
      if (
        !Array.isArray(data?.invitations) ||
        !data.invitations.length ||
        data.invitations.some((item) => !item.code)
      ) {
        throw new Error(
          text(
            '创建响应缺少邀请码，请刷新核对，勿重复提交',
            'Creation response is missing codes. Refresh to check before creating again.'
          )
        );
      }
      if (issuedRevision === codeRevision.current)
        setCodes(
          data.invitations.map(({ id, code }) => ({
            id,
            code,
            expires_at: expiresAt,
          }))
        );
      if (!creationMode && data.summary) {
        onSummaryChange?.(data.summary);
      }
      setNotice(
        text(
          `已创建 ${data.invitations.length} 个邀请码`,
          `Created ${data.invitations.length} invitation codes`
        )
      );
      refreshAfterMutation();
    } catch (failure) {
      if (mounted.current) {
        refreshAfterMutation();
        setError(
          failureMessage(
            failure,
            text(
              '创建失败，请刷新核对后重试',
              'Creation failed. Refresh before trying again.'
            )
          )
        );
      }
    } finally {
      pending.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  const revoke = async () => {
    if (!revokeTarget || pending.current) return;
    pending.current = true;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const response = await API.delete(
        `${endpoint}/${encodeURIComponent(revokeTarget.id)}`,
        invitationRequestOptions
      );
      if (!mounted.current) return;
      if (!response?.data?.success)
        throw new Error(
          response?.data?.message ||
            text('撤销失败，请重试', 'Revocation failed. Try again.')
        );
      codeRevision.current += 1;
      setCodes((current) =>
        current.filter((item) => item.id !== revokeTarget.id)
      );
      setRevokeTarget(null);
      setNotice(text('邀请码已撤销', 'Invitation revoked'));
      refreshAfterMutation();
    } catch (failure) {
      if (mounted.current)
        setError(
          failureMessage(
            failure,
            text('撤销失败，请重试', 'Revocation failed. Try again.')
          )
        );
    } finally {
      pending.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  const copyCodes = async (links) => {
    const copiedRevision = codeRevision.current;
    if (!codes.length) return;
    setError('');
    setNotice('');
    try {
      await navigator.clipboard.writeText(
        codes
          .map(({ code }) =>
            links
              ? `${window.location.origin}/register#invite=${encodeURIComponent(
                  code
                )}`
              : code
          )
          .join('\n')
      );
      if (mounted.current && copiedRevision === codeRevision.current)
        setNotice(
          links
            ? text('注册链接已复制', 'Registration links copied')
            : text('邀请码已复制', 'Invitation codes copied')
        );
    } catch (_) {
      if (mounted.current && copiedRevision === codeRevision.current)
        setError(
          text(
            '复制失败，请手动选择邀请码复制',
            'Copy failed. Select and copy the codes manually.'
          )
        );
    }
  };
  const statuses = {
    active: text('可用', 'Active'),
    used: text('已使用', 'Used'),
    revoked: text('已撤销', 'Revoked'),
    expired: text('已过期', 'Expired'),
  };
  const statusOf = (item) => {
    if (item.status !== 'active') return item.status;
    if (item.expires_at && item.expires_at <= now) return 'expired';
    if (item.remaining_uses === 0) return 'used';
    return 'active';
  };
  const usageOf = (item) => {
    const max = item.max_uses ?? 1;
    const used = item.used_count ?? (item.status === 'used' ? 1 : 0);
    return [max, used, item.remaining_uses ?? Math.max(0, max - used)];
  };
  const loadState = (result, retry) =>
    result.loading ? (
      <div role='status' className='invitation-state-v108'>
        <Loader active inline='centered' size='small' />
        {text('加载中', 'Loading')}
      </div>
    ) : result.error ? (
      <Message negative role='alert'>
        <p>{result.error}</p>
        <Button size='small' onClick={retry}>
          <Icon name='refresh' />
          {text('重试', 'Retry')}
        </Button>
      </Message>
    ) : !result.data?.items.length ? (
      <Message content={text('暂无记录', 'No records')} />
    ) : null;

  return (
    <div className='organization-invitations-v108'>
      <section aria-label={text('邀请码管理', 'Invitation management')}>
        <div className='invitation-heading-v108'>
          <Header as='h3'>
            {creationMode
              ? text('组织创建的邀请链接', 'Organization creation links')
              : text('邀请码', 'Invitation codes')}
          </Header>
          <Button
            icon
            type='button'
            title={text('刷新邀请码', 'Refresh invitations')}
            aria-label={text('刷新邀请码', 'Refresh invitations')}
            onClick={refresh}
            disabled={saving}
          >
            <Icon name='refresh' />
          </Button>
        </div>
        {!creationMode && (
          <dl
            className='invitation-summary-v108'
            aria-label={text('组织席位', 'Organization seats')}
          >
            {[
              [text('已使用', 'Used seats'), 'seat_used'],
              [text('已预留', 'Reserved seats'), 'reserved'],
              [text('可用', 'Available seats'), 'available'],
            ].map(([label, field]) => (
              <div key={field}>
                <dt>{label}</dt>
                <dd>{summary?.[field] ?? '-'}</dd>
              </div>
            ))}
          </dl>
        )}
        {!creationMode && (
          <dl
            className='invitation-funds-v1010'
            aria-label={text('邀请码资金', 'Invitation funds')}
          >
            <div>
              <dt>{text('主账号可用（USD）', 'Parent available (USD)')}</dt>
              <dd>{funds ? `$${funds.available_usd}` : '-'}</dd>
            </div>
            <div>
              <dt>{text('邀请码预留（USD）', 'Invitation reserved (USD)')}</dt>
              <dd>{funds ? `$${funds.reserved_usd}` : '-'}</dd>
            </div>
          </dl>
        )}
        {notice && <Message positive role='status' content={notice} />}
        {error && !revokeTarget && (
          <Message negative role='alert' content={error} />
        )}
        <Form
          onSubmit={create}
          className={`invitation-create-v108${
            creationMode ? '' : ' invitation-credit-create-v109'
          }`}
        >
          <Form.Input
            id='invitation-count-v108'
            label={text('数量', 'Count')}
            name='invitation_count'
            type='number'
            inputMode='numeric'
            min={1}
            max={100}
            step={1}
            required
            value={count}
            onChange={(_, { value }) => setCount(value)}
          />
          <Form.Input
            id='invitation-max-uses-v109'
            label={text('每个码可用次数', 'Uses per code')}
            name='invitation_max_uses'
            type='number'
            inputMode='numeric'
            min={1}
            max={maximumUses}
            step={1}
            required
            value={maxUses}
            onChange={(_, { value }) => setMaxUses(value)}
          />
          <Form.Input
            id='invitation-days-v108'
            label={text('有效期（天）', 'Expires in (days)')}
            name='invitation_days'
            type='number'
            inputMode='numeric'
            min={1}
            max={365}
            step={1}
            required
            value={days}
            onChange={(_, { value }) => setDays(value)}
          />
          <Form.Input
            id='invitation-note-v108'
            label={text('备注', 'Note')}
            name='invitation_note'
            maxLength={200}
            value={note}
            onChange={(_, { value }) => setNote(value)}
          />
          {!creationMode && (
            <Form.Input
              id='invitation-initial-credit-v109'
              label={text(
                '每位用户注册充值（USD）',
                'Credit per registration (USD)'
              )}
              name='invitation_initial_credit'
              type='text'
              inputMode='decimal'
              maxLength={32}
              required
              value={initialCredit}
              onChange={(_, { value }) => setInitialCredit(value)}
            />
          )}
          <Button
            primary
            type='submit'
            loading={saving && !revokeTarget}
            disabled={
              saving ||
              list.loading ||
              Boolean(list.error) ||
              (!creationMode &&
                (!Number.isInteger(summary?.available) ||
                  summary.available < 1))
            }
          >
            <Icon name='plus' />
            {text('创建邀请码', 'Create invitations')}
          </Button>
          {!creationMode && (
            <output
              className='invitation-total-v1010'
              aria-live='polite'
              aria-label={text('本次预留', 'Reserve on creation')}
            >
              <span>{text('本次预留', 'Reserve on creation')}</span>
              <strong>
                {reserveTotal === null ? '-' : `$${reserveTotal}`}
              </strong>
              <span>
                {text(
                  '未领取金额：到期或撤销后释放',
                  'Unclaimed funds: released on expiry or revocation'
                )}
              </span>
            </output>
          )}
        </Form>
        {codes.length > 0 && (
          <section
            className='invitation-issued-v108'
            aria-label={text(
              '新邀请码（仅显示一次）',
              'New codes (shown once)'
            )}
          >
            <div className='invitation-heading-v108'>
              <Header as='h4'>
                {text('新邀请码（仅显示一次）', 'New codes (shown once)')}
              </Header>
              <Button
                icon
                type='button'
                title={text('隐藏邀请码', 'Dismiss codes')}
                aria-label={text('隐藏邀请码', 'Dismiss codes')}
                onClick={clearCodes}
              >
                <Icon name='close' />
              </Button>
            </div>
            <textarea
              aria-label={text('新邀请码', 'New invitation codes')}
              readOnly
              rows={Math.min(6, codes.length + 1)}
              value={codes.map(({ code }) => code).join('\n')}
              spellCheck={false}
            />
            <div className='invitation-copy-v108'>
              <Button type='button' onClick={() => copyCodes(false)}>
                <Icon name='copy outline' />
                {text('复制邀请码', 'Copy codes')}
              </Button>
              <Button type='button' onClick={() => copyCodes(true)}>
                <Icon name='linkify' />
                {text('复制注册链接', 'Copy registration links')}
              </Button>
            </div>
          </section>
        )}
        <Form
          className='invitation-filters-v108'
          onSubmit={(event) => {
            event.preventDefault();
            changeQuery({ ...query, page: 1, q: search.trim() });
          }}
        >
          <Form.Field>
            <label htmlFor='invitation-status-v108'>
              {text('状态', 'Status')}
            </label>
            <select
              id='invitation-status-v108'
              value={query.status}
              onChange={(event) =>
                changeQuery({ ...query, page: 1, status: event.target.value })
              }
            >
              <option value=''>{text('全部状态', 'All statuses')}</option>
              {Object.entries(statuses).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Form.Field>
          <Form.Input
            id='invitation-search-v108'
            label={text('搜索邀请码或备注', 'Search code or note')}
            name='invitation_search'
            maxLength={200}
            value={search}
            onChange={(_, { value }) => setSearch(value)}
          />
          <Button
            icon
            type='submit'
            title={text('搜索邀请码', 'Search invitations')}
            aria-label={text('搜索邀请码', 'Search invitations')}
          >
            <Icon name='search' />
          </Button>
        </Form>
        {loadState(list, refresh)}
        {!list.loading && !list.error && list.data?.items.length > 0 && (
          <div
            className='invitation-table-scroll-v108'
            role='region'
            tabIndex={0}
            aria-label={text('邀请码列表', 'Invitation list')}
          >
            <Table
              basic='very'
              compact
              unstackable
              className='invitation-usage-table-v109'
            >
              <Table.Header>
                <Table.Row>
                  {[
                    text('邀请码前缀', 'Code prefix'),
                    text('状态', 'Status'),
                    text('可用次数', 'Maximum uses'),
                    text('已用次数', 'Used count'),
                    text('剩余次数', 'Remaining uses'),
                    ...(!creationMode
                      ? [text('每次充值（USD）', 'Credit per use (USD)')]
                      : []),
                    text('备注', 'Note'),
                    text('到期时间', 'Expires at'),
                    text('操作', 'Actions'),
                  ].map((label) => (
                    <Table.HeaderCell key={label}>{label}</Table.HeaderCell>
                  ))}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {list.data.items.map((item) => (
                  <Table.Row key={item.id}>
                    <Table.Cell>
                      <code>{item.code_prefix}</code>
                    </Table.Cell>
                    <Table.Cell>
                      <Label
                        basic
                        size='small'
                        color={
                          statusOf(item) === 'active' ? 'green' : undefined
                        }
                      >
                        {statuses[statusOf(item)] || item.status}
                      </Label>
                    </Table.Cell>
                    {usageOf(item).map((value, index) => (
                      <Table.Cell key={index}>{value}</Table.Cell>
                    ))}
                    {!creationMode && (
                      <Table.Cell>
                        ${item.initial_credit_usd ?? '0.00'}
                        {item.funding_mode === 'reserved' && (
                          <div className='invitation-credit-note-v1010'>
                            {text('预留', 'Reserved')} $
                            {statusOf(item) === 'active'
                              ? item.reserved_credit_usd
                              : '0.00'}
                          </div>
                        )}
                        {item.funding_mode === 'on_registration' && (
                          <div className='invitation-credit-note-v1010'>
                            {text(
                              '旧码：注册时划拨',
                              'Legacy: transfer on signup'
                            )}
                          </div>
                        )}
                      </Table.Cell>
                    )}
                    <Table.Cell>{item.note || '-'}</Table.Cell>
                    <Table.Cell>
                      {dateLabel(item.expires_at, i18n?.language)}
                    </Table.Cell>
                    <Table.Cell>
                      {statusOf(item) === 'active' && (
                        <Button
                          icon
                          type='button'
                          title={text('撤销', 'Revoke')}
                          aria-label={`${text('撤销', 'Revoke')} ${
                            item.code_prefix
                          }`}
                          disabled={saving}
                          onClick={() => {
                            setError('');
                            setRevokeTarget(item);
                          }}
                        >
                          <Icon name='ban' />
                        </Button>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
        {!list.error && (
          <ListPager
            query={query}
            data={list.data}
            loading={list.loading}
            onChange={changeQuery}
            text={text}
          />
        )}
      </section>
      <section
        className='invitation-redemptions-v108'
        aria-label={text('兑换记录', 'Redemption records')}
      >
        <div className='invitation-heading-v108'>
          <Header as='h3'>{text('兑换记录', 'Redemption records')}</Header>
          <Button
            icon
            type='button'
            title={text('刷新兑换记录', 'Refresh redemptions')}
            aria-label={text('刷新兑换记录', 'Refresh redemptions')}
            onClick={() => setRecordRevision((value) => value + 1)}
          >
            <Icon name='refresh' />
          </Button>
        </div>
        <Form
          className='invitation-filters-v108'
          onSubmit={(event) => {
            event.preventDefault();
            setRecordQuery({ ...recordQuery, page: 1, q: recordSearch.trim() });
          }}
        >
          <Form.Input
            id='redemption-search-v108'
            label={text('搜索账号或邀请码', 'Search account or code')}
            name='redemption_search'
            maxLength={200}
            value={recordSearch}
            onChange={(_, { value }) => setRecordSearch(value)}
          />
          <Button
            icon
            type='submit'
            title={text('搜索兑换记录', 'Search redemptions')}
            aria-label={text('搜索兑换记录', 'Search redemptions')}
          >
            <Icon name='search' />
          </Button>
        </Form>
        {loadState(records, () => setRecordRevision((value) => value + 1))}
        {!records.loading &&
          !records.error &&
          records.data?.items.length > 0 && (
            <div
              className='invitation-table-scroll-v108'
              role='region'
              tabIndex={0}
              aria-label={text('兑换记录列表', 'Redemption list')}
            >
              <Table basic='very' compact unstackable>
                <Table.Header>
                  <Table.Row>
                    {[
                      text('账号', 'Account'),
                      ...(creationMode
                        ? [text('组织名称', 'Organization name')]
                        : []),
                      text('兑换时间', 'Redeemed at'),
                      text('邀请码前缀', 'Code prefix'),
                      ...(!creationMode
                        ? [text('注册充值（USD）', 'Registration credit (USD)')]
                        : []),
                    ].map((label) => (
                      <Table.HeaderCell key={label}>{label}</Table.HeaderCell>
                    ))}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {records.data.items.map((item) => (
                    <Table.Row
                      key={item.id}
                      data-invitation-id={item.invitation_id}
                    >
                      <Table.Cell>{item.redeemed_username || '-'}</Table.Cell>
                      {creationMode && (
                        <Table.Cell>{item.organization_name || '-'}</Table.Cell>
                      )}
                      <Table.Cell>
                        {dateLabel(item.redeemed_at, i18n?.language)}
                      </Table.Cell>
                      <Table.Cell>
                        <code>{item.code_prefix}</code>
                      </Table.Cell>
                      {!creationMode && (
                        <Table.Cell>
                          ${item.initial_credit_usd ?? '0.00'}
                        </Table.Cell>
                      )}
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
        {!records.error && (
          <ListPager
            query={recordQuery}
            data={records.data}
            loading={records.loading}
            onChange={setRecordQuery}
            text={text}
          />
        )}
      </section>
      <Modal
        open={Boolean(revokeTarget)}
        size='tiny'
        onClose={() => {
          if (!pending.current) setRevokeTarget(null);
        }}
        closeOnDimmerClick={!saving}
        closeOnEscape={!saving}
      >
        <Modal.Header>{text('撤销邀请码', 'Revoke invitation')}</Modal.Header>
        <Modal.Content>
          <p>
            {text('确认撤销此邀请码？', 'Revoke this invitation?')}{' '}
            <code>{revokeTarget?.code_prefix}</code>
          </p>
          {revokeTarget?.funding_mode === 'reserved' && (
            <p>
              {text('释放未领取金额（USD）', 'Release unclaimed funds (USD)')}:
              $
              {statusOf(revokeTarget) === 'active'
                ? revokeTarget.reserved_credit_usd
                : '0.00'}
            </p>
          )}
          {error && <Message negative role='alert' content={error} />}
        </Modal.Content>
        <Modal.Actions>
          <Button
            type='button'
            disabled={saving}
            onClick={() => setRevokeTarget(null)}
          >
            {text('取消', 'Cancel')}
          </Button>
          <Button
            negative
            type='button'
            loading={saving}
            disabled={saving}
            onClick={revoke}
          >
            <Icon name='ban' />
            {text('确认撤销', 'Confirm revoke')}
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
}

export default function OrganizationInvitationsV108(props) {
  if (props.mode === 'organizationCreation' && !props.admin) return null;
  return (
    <InvitationWorkspace
      key={`${props.mode || 'member'}:${Boolean(props.admin)}:${
        props.organizationId
      }`}
      {...props}
    />
  );
}
